import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideHttpClient, withFetch } from '@angular/common/http';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideClientHydration,
  withEventReplay,
  withHttpTransferCacheOptions,
} from '@angular/platform-browser';

import { routes } from './app.routes';
import { loadBlogSlugs } from './core/api/blog-slug-registry';
import { loadDiscountTypeSuffixes } from './core/api/discount-type-registry';
import { loadPageSeoOverrides } from './core/seo/page-seo-registry';
import { AnalyticsService } from './core/analytics/analytics.service';
import { CopyProtectionService } from './core/copy-protection/copy-protection.service';
import { ScrollRestorationService } from './core/scroll/scroll-restoration.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    // Must resolve before the router's first navigation: `discountPageMatcher` decides whether a
    // single-segment URL is a discount page or a store page, and it reads the suffix list this
    // loads. It fails soft — see the registry for why.
    provideAppInitializer(loadDiscountTypeSuffixes),
    // Same reason: `/blogs/<slug>` is a category for some slugs and a hub for others, and the
    // matchers that decide read the registry this fills. Also fails soft.
    provideAppInitializer(loadBlogSlugs),
    // Admin meta overrides for the pages with no admin screen of their own. `SeoService.apply()` is
    // synchronous, so the overrides have to be in memory before the first page sets its tags. Also
    // fails soft — a page without an override simply keeps its built-in wording.
    provideAppInitializer(loadPageSeoOverrides),
    provideRouter(
      routes,
      // Scroll position is handled by `ScrollRestorationService`, not the router — see that file
      // for why the router cannot do it (it disables the browser's native reload restoration, and
      // it restores before the page's data has loaded, which clamps the offset). Left at
      // `disabled` so the two never fight; `anchorScrolling` is a separate concern and stays on.
      withInMemoryScrolling({ scrollPositionRestoration: 'disabled', anchorScrolling: 'enabled' }),
    ),
    provideAppInitializer(() => inject(ScrollRestorationService).init()),
    // UX-level copy protection for the public pages. No-ops during SSR, so the server-rendered HTML
    // — and therefore what a crawler sees — is byte-identical with and without it.
    provideAppInitializer(() => inject(CopyProtectionService).init()),
    // GA4. Registered after provideRouter above, because it subscribes to router events to send a
    // page_view per navigation — the pasted gtag snippet sends one for the whole session.
    provideAppInitializer(() => inject(AnalyticsService).init()),
    // withFetch() makes HttpClient use the Fetch API, which is what the SSR runtime supports.
    provideHttpClient(withFetch()),
    provideAnimationsAsync(),
    provideClientHydration(
      // Replays clicks/inputs that happened before hydration finished, so a server-rendered page
      // feels interactive immediately instead of dropping early taps.
      withEventReplay(),
      // Lets a `@defer (hydrate on …)` block ship its server-rendered HTML as normal but postpone
      // hydration until the trigger fires. /storelist is the case this exists for: it renders ~700
      // store cards, and hydrating all of them up front is what made the page slow. The markup —
      // and so every store link a crawler follows — is still in the initial response.
      // NOT `withIncrementalHydration()`. It was tried for /storelist (27 alphabet sections × 27
      // store cards, ~1300 links) with `@defer (hydrate on viewport)` on each section, and measured
      // clearly worse on a production build serving the identical page:
      //
      //     without: FCP 508ms, domComplete  770ms, load  780ms, document 684 KB
      //     with:    FCP 712ms, domComplete 2878ms, load 2890ms, document 690 KB
      //
      // The payload was unchanged — the cost is the per-deferred-block bookkeeping at runtime, and
      // it scales with the number of blocks, which is exactly what a directory page has many of.
      // Do not re-add it here without re-measuring /storelist on a production build.
      // The server already fetched this page's data; serialize those GET responses into the HTML so
      // the browser reuses them instead of re-issuing every API call on hydration (spec §12).
      withHttpTransferCacheOptions({
        includePostRequests: false,
        // Never transfer-cache request headers — see the `hasRated` note below.
        includeHeaders: [],
      }),
    ),
  ],
};
